import { ehDiaUtil } from '@/produtividade/expediente';

/**
 * ★★ WO lote 2b — A GRADE MENSAL, no formato da planilha do dono.
 *
 * ★ Só DESENHA. Toda conta vem de `montarGrade` (puro, 28 casos). ⚠ Se um número for
 * calculado aqui dentro, ele não tem teste — e a tela passa a ser uma segunda definição.
 *
 * ★ Decisões lidas do desenho aprovado (`docs/wo-desenho-aprovado.html`):
 *   - cabeçalho de DUAS linhas: inicial do dia da semana, depois o número;
 *   - "Sem N" com `rowspan=2` depois de cada semana; "Mês" no fim;
 *   - a primeira coluna é `sticky` — ⚠ com 26 colunas de dia a tabela rola, e sem isso o
 *     leitor perde de quem é a linha (é o defeito que a VI-1 mediu na lista de leads);
 *     ★★ WR (4/out/2026): a linha de TÍTULO de seção não estava coberta por isso, porque
 *     era um `colSpan` só — ver o comentário dela lá embaixo;
 *   - coluna de hoje com **borda azul** nos dois lados;
 *   - a célula de META fica **vazia** no futuro.
 */

const SIGLA = { seg: 'S', ter: 'T', qua: 'Q', qui: 'Q', sex: 'S', sab: 'S' };
const inicialDoDia = (dia) => {
  const [a, m, d] = dia.split('-').map(Number);
  return SIGLA[['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'][new Date(Date.UTC(a, m - 1, d)).getUTCDay()]] ?? '';
};
const numeroDoDia = (dia) => String(Number(dia.slice(8)));

export default function Grade({ grade, config, podeVerTudo }) {
  if (!grade) return null;
  const { semanas, dias, hoje, blocos, foraDaGrade, semMetas } = grade;
  /** ★ Indicadores (01/11/2026): na SEMANA não há coluna "Mês" — o total da semana é o total. */
  const ehSemana = grade.visao === 'semana';
  const colunas = dias.length + semanas.length + (ehSemana ? 1 : 2);
  const ehHoje = (d) => d === hoje;
  /** ★ 02/11/2026 — o feriado sem meta: a coluna fica cinza e o cabeçalho diz "Fer". */
  const feriados = new Set(grade.feriados ?? []);
  const ehFeriadoDaGrade = (d) => feriados.has(d);

  /** As classes de uma célula de realizado, pelo que o módulo puro decidiu. */
  const classe = (base, cor, d) =>
    [base, cor, ehHoje(d) ? 'hoje' : ''].filter(Boolean).join(' ');

  const linhaMeta = (l, rotulo) => (
    <tr key={`${l.id}-meta`}>
      <td className="lbl"><span className="who">{l.nome}</span> · {rotulo}</td>
      {semanas.map((w, i) => (
        <Celulas key={i}>
          {w.map((d) => (
            <td key={d} className={classe('meta', '', d)}>
              {/* ★ vazio no futuro: meta de amanhã não é cobrança de hoje */}
              {l.celulas[d].futuro || !(l.metaDiariaPorDia?.[d] ?? l.metaDiaria) ? '' : l.celulas[d].meta}
            </td>
          ))}
          <td className="sem">{l.semanas[i].metaAcum || ''}</td>
        </Celulas>
      ))}
      {!ehSemana && <td className="mes">{l.mes.metaAcum || ''}</td>}
    </tr>
  );

  const linhaRealizado = (l) => (
    <tr key={`${l.id}-real`}>
      <td className="lbl sub">realizado</td>
      {semanas.map((w, i) => (
        <Celulas key={i}>
          {w.map((d) => {
            const c = l.celulas[d];
            return (
              <td key={d} className={classe('r', c.cor, d)}>
                {c.futuro ? '–' : c.valor}
                {c.pendente > 0 && (
                  <sup className="p" title="agendamentos sem baixa — não contam na meta">{c.pendente}</sup>
                )}
              </td>
            );
          })}
          <td className={['sem', l.semanas[i].cor].filter(Boolean).join(' ')}>{l.semanas[i].valor}</td>
        </Celulas>
      ))}
      {!ehSemana && <td className={['mes', l.mes.cor].filter(Boolean).join(' ')}>{l.mes.valor}</td>}
    </tr>
  );

  return (
    <>
      <div className="tabela-envolve grade-envolve">
        {/* ★★ 02/11/2026 — na SEMANA a tabela não estica até a largura da página: com 6 dias,
            o `min-width` do mês jogava a sobra inteira na coluna do nome e os dias ficavam
            longe dos nomes. ★ O mês fica como estava (classe só na semana). */}
        <table className={ehSemana ? 'grade grade-semana' : 'grade'}>
          <thead>
            <tr>
              <th className="lbl" rowSpan={2}>Indicador</th>
              {semanas.map((w, i) => (
                <Celulas key={i}>
                  {w.map((d) => (
                    <th key={d} className={classe('dow', ehFeriadoDaGrade(d) ? 'feriado' : '', d)}
                      title={ehFeriadoDaGrade(d) ? 'Feriado: sem meta para a equipe' : undefined}>
                      {ehFeriadoDaGrade(d) ? 'Fer' : inicialDoDia(d)}
                    </th>
                  ))}
                  <th className="sem" rowSpan={2}>{ehSemana ? 'Semana' : `Sem ${i + 1}`}</th>
                </Celulas>
              ))}
              {!ehSemana && <th className="mes" rowSpan={2}>Mês</th>}
            </tr>
            <tr>
              {semanas.map((w, i) => (
                <Celulas key={i}>
                  {w.map((d) => (
                    <th key={d} className={[ehHoje(d) ? 'hoje' : '', ehDiaUtil(d, config) && !ehFeriadoDaGrade(d) ? '' : 'nao-util', ehFeriadoDaGrade(d) ? 'feriado' : ''].filter(Boolean).join(' ')}>
                      {numeroDoDia(d)}
                    </th>
                  ))}
                </Celulas>
              ))}
            </tr>
          </thead>
          <tbody>
            {blocos.map((b) => (
              b.linhas.length === 0 ? null : (
                <Celulas key={b.id}>
                  {/* ★★ WR (4/out/2026) — o título era UM `th` com `colSpan` atravessando a
                      tabela. ⚠ Cabia na tela, mas ao rolar para a direita o texto saía pela
                      esquerda e o dono via "TATIVAS EM LEADS DISTINTOS)". ★ Agora são duas
                      células: a primeira grudada na coluna fixa (com o texto inteiro) e o
                      resto vazio só para levar o fundo colorido até o fim da linha. */}
                  <tr className={`sec ${b.espaco}`}>
                    <th className="sec-lbl"><span>{b.titulo}</span></th>
                    <th className="sec-resto" colSpan={colunas - 1} />
                  </tr>
                  {b.linhas.flatMap((l) => [linhaMeta(l, 'meta'), linhaRealizado(l)])}
                  {b.total && (
                    <tr>
                      {/* ★ sem cor, de propósito: somar metas de pessoas diferentes esconderia
                          quem ficou atrás (duas pessoas, uma a 200% e outra a 20%, dariam verde). */}
                      <td className="lbl">Total</td>
                      {semanas.map((w, i) => (
                        <Celulas key={i}>
                          {w.map((d) => (
                            <td key={d} className={classe('r', '', d)}>
                              {b.total.celulas[d].futuro ? '–' : b.total.celulas[d].valor}
                            </td>
                          ))}
                          <td className="sem">{b.total.semanas[i]}</td>
                        </Celulas>
                      ))}
                      {!ehSemana && <td className="mes">{b.total.mes}</td>}
                    </tr>
                  )}
                </Celulas>
              )
            ))}
          </tbody>
        </table>
      </div>

      <div className="grade-legenda">
        <span><i className="am" />meta do dia</span>
        <span><i className="vd" />realizado ≥ meta</span>
        <span><i className="vm" />abaixo da meta</span>
        <span><i className="cz" />nada registrado</span>
        <span><sup className="p">1</sup> agendamento sem baixa (não conta na meta)</span>
        <span>total colorido pela <strong>meta acumulada até hoje</strong></span>
        {hoje && <span>coluna com borda azul = hoje</span>}
        {feriados.size > 0 && <span><strong>Fer</strong> = feriado, sem meta (o que foi feito conta)</span>}
      </div>

      {semMetas && (
        <p className="aviso">
          <strong>Nenhuma meta por dia configurada ainda.</strong> Sem alvo, a grade mostra o
          que foi feito mas não diz se está bom — as linhas de meta ficam vazias e nada é
          colorido. As metas por dia (ligações e follow-ups) entram na tela de Metas no
          próximo lote.
        </p>
      )}

      {/* ★★ O que a grade NÃO mostra, dito na tela. A clínica não atende domingo, então
          domingo não vira coluna — mas trabalho que existiu não pode sumir calado.
          ★ Conta só registros DA EQUIPE: medido em setembro, dos 254 registros de domingo
          213 eram robô (migração, webhook, importação) e 41 eram gente. Dizer "254" seria
          anunciar trabalho perdido que não existiu. */}
      {foraDaGrade.registros > 0 && (
        <p className="campo-ajuda">
          <strong>{foraDaGrade.registros}</strong>{' '}
          {/* ★ 02/11/2026 — a SDR vê só os registros DELA (a conta é de `gradeDoMes`/`gradeDaSemana`) */}
          {podeVerTudo ? 'registro(s) da equipe' : 'registro(s) seu(s)'} {ehSemana ? 'nesta semana' : 'neste mês'} caíram em{' '}
          <strong>domingo</strong> e não aparecem na grade — a clínica não atende domingo, e a
          semana vai de segunda a sábado. Eles continuam gravados na linha do tempo de cada lead.
        </p>
      )}

      {!podeVerTudo && (
        <p className="campo-ajuda">Você vê somente a sua linha. O gestor vê a equipe inteira.</p>
      )}
    </>
  );
}

/** Fragmento com chave — só para manter o JSX legível nas repetições por semana. */
function Celulas({ children }) {
  return <>{children}</>;
}
